import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// GJYL Public Hub - System space ID (consistent across all users)
const GJYL_PUBLIC_HUB_ID = 'gjyl-public-hub';

// Admin user ID (Paul) - only this user can post in Announcements
const ADMIN_USER_ID = '7fc52c66-1809-4617-ae03-d82f30f86a22';

export interface PublicChannel {
  id: string;
  name: string;
  type: 'text' | 'announcements';
  description?: string;
  unread_count: number;
  is_admin_only: boolean;
}

export interface PublicMessage {
  id: string;
  author: string;
  author_id: string;
  avatar_url?: string;
  text: string;
  time: string;
  is_admin: boolean;
}

interface UsePublicChannelsReturn {
  channels: PublicChannel[];
  messages: Record<string, PublicMessage[]>;
  activeChannel: string;
  setActiveChannel: (id: string) => void;
  sendMessage: (text: string) => Promise<boolean>;
  loading: boolean;
  isAdmin: boolean;
  currentUser: { id: string; username: string; avatar_url?: string } | null;
}

// Default channels for the GJYL Public Hub
const DEFAULT_CHANNELS: PublicChannel[] = [
  {
    id: 'announcements',
    name: 'Announcements',
    type: 'announcements',
    description: 'Official updates from GJYL',
    unread_count: 1,
    is_admin_only: true,
  },
  {
    id: 'general',
    name: 'General',
    type: 'text',
    description: 'Chat with the GJYL community',
    unread_count: 0,
    is_admin_only: false,
  },
  {
    id: 'introductions',
    name: 'Introductions',
    type: 'text',
    description: 'Say hello to the community',
    unread_count: 0,
    is_admin_only: false,
  },
  {
    id: 'feedback',
    name: 'Feedback',
    type: 'text',
    description: 'Share your thoughts on GJYL',
    unread_count: 0,
    is_admin_only: false,
  },
];

// Welcome message from Paul (the admin)
const WELCOME_MESSAGES: Record<string, PublicMessage[]> = {
  announcements: [
    {
      id: 'welcome-1',
      author: 'Paul',
      author_id: ADMIN_USER_ID,
      text: 'Welcome to GJYL! This is your new home for real conversations.',
      time: formatTime(new Date()),
      is_admin: true,
    },
    {
      id: 'welcome-2',
      author: 'Paul',
      author_id: ADMIN_USER_ID,
      text: 'GJYL is built different. No ads. No algorithms. No noise. Just you and your friends, the way chat should be.',
      time: formatTime(new Date()),
      is_admin: true,
    },
    {
      id: 'welcome-3',
      author: 'Paul',
      author_id: ADMIN_USER_ID,
      text: 'Head over to #General to meet the community, or create your own Space to hang with friends. The future of chat starts here.',
      time: formatTime(new Date()),
      is_admin: true,
    },
  ],
  general: [
    {
      id: 'general-1',
      author: 'GJYL Bot',
      author_id: 'system',
      text: 'Welcome to #General! This is the place to chat with the GJYL community.',
      time: formatTime(new Date()),
      is_admin: false,
    },
  ],
  introductions: [],
  feedback: [],
};

function formatTime(date: Date): string {
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
}

export function usePublicChannels(): UsePublicChannelsReturn {
  const [channels] = useState<PublicChannel[]>(DEFAULT_CHANNELS);
  const [messages, setMessages] = useState<Record<string, PublicMessage[]>>(WELCOME_MESSAGES);
  const [activeChannel, setActiveChannel] = useState<string>('announcements');
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<{ id: string; username: string; avatar_url?: string } | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Load current user
  useEffect(() => {
    const loadUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Get profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('username, avatar_url')
            .eq('id', user.id)
            .single();

          setCurrentUser({
            id: user.id,
            username: profile?.username || user.email?.split('@')[0] || 'User',
            avatar_url: profile?.avatar_url,
          });

          // Check if admin
          setIsAdmin(user.id === ADMIN_USER_ID);
        }
      } catch (error) {
        console.error('Error loading user:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Load messages from Supabase (public hub messages)
  useEffect(() => {
    const loadMessages = async () => {
      try {
        // Try to load from public_hub_messages table
        const { data, error } = await supabase
          .from('public_hub_messages')
          .select('*, profiles:author_id(username, avatar_url)')
          .order('created_at', { ascending: true })
          .limit(100);

        if (!error && data && data.length > 0) {
          // Group messages by channel
          const grouped: Record<string, PublicMessage[]> = {
            announcements: [...WELCOME_MESSAGES.announcements],
            general: [...WELCOME_MESSAGES.general],
            introductions: [],
            feedback: [],
          };

          data.forEach((msg: any) => {
            const channel = msg.channel_id || 'general';
            if (grouped[channel]) {
              grouped[channel].push({
                id: msg.id,
                author: msg.profiles?.username || 'Unknown',
                author_id: msg.author_id,
                avatar_url: msg.profiles?.avatar_url,
                text: msg.content,
                time: formatTime(new Date(msg.created_at)),
                is_admin: msg.author_id === ADMIN_USER_ID,
              });
            }
          });

          setMessages(grouped);
        }
      } catch (error) {
        // Table might not exist yet, use default messages
        console.log('Using default messages (public_hub_messages table may not exist)');
      }
    };

    loadMessages();

    // Subscribe to new messages
    const subscription = supabase
      .channel('public-hub-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'public_hub_messages',
        },
        (payload: any) => {
          const msg = payload.new;
          const channel = msg.channel_id || 'general';

          setMessages(prev => ({
            ...prev,
            [channel]: [
              ...(prev[channel] || []),
              {
                id: msg.id,
                author: msg.author_name || 'Unknown',
                author_id: msg.author_id,
                text: msg.content,
                time: formatTime(new Date(msg.created_at)),
                is_admin: msg.author_id === ADMIN_USER_ID,
              },
            ],
          }));
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Send message function
  const sendMessage = useCallback(async (text: string): Promise<boolean> => {
    if (!text.trim() || !currentUser) return false;

    const channel = channels.find(c => c.id === activeChannel);

    // Check if channel is admin-only
    if (channel?.is_admin_only && !isAdmin) {
      console.warn('Cannot send message to admin-only channel');
      return false;
    }

    // Optimistic update
    const newMsg: PublicMessage = {
      id: crypto.randomUUID(),
      author: currentUser.username,
      author_id: currentUser.id,
      avatar_url: currentUser.avatar_url,
      text: text.trim(),
      time: formatTime(new Date()),
      is_admin: isAdmin,
    };

    setMessages(prev => ({
      ...prev,
      [activeChannel]: [...(prev[activeChannel] || []), newMsg],
    }));

    // Try to persist to database
    try {
      await supabase.from('public_hub_messages').insert({
        id: newMsg.id,
        channel_id: activeChannel,
        author_id: currentUser.id,
        author_name: currentUser.username,
        content: text.trim(),
        created_at: new Date().toISOString(),
      });
    } catch (error) {
      // Table might not exist, message still shows locally
      console.log('Message stored locally (database table may not exist)');
    }

    return true;
  }, [activeChannel, channels, currentUser, isAdmin]);

  return {
    channels,
    messages,
    activeChannel,
    setActiveChannel,
    sendMessage,
    loading,
    isAdmin,
    currentUser,
  };
}

export default usePublicChannels;
